import React from 'react';
import { Check, X, Eye, User, BookOpen, Heart, GraduationCap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { getLocalizedText } from '../../utils/helpers';

export const EditsTab = ({ 
  pendingEdits, 
  pendingEditsCount,
  onOpenPreview, 
  onAction 
}) => {
  if (pendingEditsCount === 0) {
    return (
      <div className="text-center py-12">
        <Check className="h-12 w-12 text-green-500 mx-auto mb-4" />
        <p className="text-gray-500">No hay modificaciones pendientes</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Service Edits */}
      {pendingEdits?.services?.length > 0 && (
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Heart className="h-5 w-5 text-rose-500" />
            Servicios Modificados ({pendingEdits.services.length})
          </h3>
          <div className="space-y-2">
            {pendingEdits.services.map(service => (
              <div 
                key={service.id}
                onClick={() => onOpenPreview(service, 'service-edit', `/api/admin/services/${service.id}/approve-edit`)}
                className="flex items-center justify-between bg-orange-50 hover:bg-orange-100 rounded-lg p-4 cursor-pointer transition-colors border-l-4 border-orange-400"
              >
                <div className="flex items-center gap-3">
                  <img src={service.images?.[0] || '/default-service.png'} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  <div>
                    <p className="font-medium">{getLocalizedText(service.title, 'es')}</p>
                    <p className="text-sm text-gray-500">{service.user_name}</p>
                    <p className="text-xs text-orange-600">
                      Modificado: {service.edit_submitted_at ? new Date(service.edit_submitted_at).toLocaleString('es-ES') : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpenPreview(service, 'service-edit', `/api/admin/services/${service.id}/approve-edit`); }}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button size="sm" className="bg-green-500" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/services/${service.id}/approve-edit`); }}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/services/${service.id}/reject-edit`); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Course Edits */}
      {pendingEdits?.courses?.length > 0 && (
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-emerald-500" />
            Cursos Modificados ({pendingEdits.courses.length})
          </h3>
          <div className="space-y-2">
            {pendingEdits.courses.map(course => (
              <div 
                key={course.id}
                onClick={() => onOpenPreview(course, 'course-edit', `/api/admin/courses/${course.id}/approve-edit`)}
                className="flex items-center justify-between bg-orange-50 hover:bg-orange-100 rounded-lg p-4 cursor-pointer transition-colors border-l-4 border-orange-400"
              >
                <div className="flex items-center gap-3">
                  <img src={course.images?.[0] || '/default-course.png'} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  <div>
                    <p className="font-medium">{getLocalizedText(course.title, 'es')}</p>
                    <p className="text-sm text-gray-500">{course.user_name}</p>
                    <p className="text-xs text-orange-600">
                      Modificado: {course.edit_submitted_at ? new Date(course.edit_submitted_at).toLocaleString('es-ES') : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpenPreview(course, 'course-edit', `/api/admin/courses/${course.id}/approve-edit`); }}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button size="sm" className="bg-green-500" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/courses/${course.id}/approve-edit`); }}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/courses/${course.id}/reject-edit`); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Teacher Profile Edits */}
      {pendingEdits?.teachers?.length > 0 && (
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-purple-500" />
            Perfiles Profesionales Modificados ({pendingEdits.teachers.length})
          </h3>
          <div className="space-y-2">
            {pendingEdits.teachers.map(teacher => (
              <div 
                key={teacher.id}
                onClick={() => onOpenPreview(teacher, 'teacher-edit', `/api/admin/teachers/${teacher.id}/approve-edit`)}
                className="flex items-center justify-between bg-orange-50 hover:bg-orange-100 rounded-lg p-4 cursor-pointer transition-colors border-l-4 border-orange-400"
              >
                <div className="flex items-center gap-3">
                  <img src={teacher.avatar || '/default-avatar.png'} alt="" className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="font-medium">{teacher.user_name}</p>
                    <p className="text-sm text-gray-500">{teacher.user_email}</p>
                    <p className="text-xs text-orange-600">
                      Modificado: {teacher.edit_submitted_at ? new Date(teacher.edit_submitted_at).toLocaleString('es-ES') : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpenPreview(teacher, 'teacher-edit', `/api/admin/teachers/${teacher.id}/approve-edit`); }}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button size="sm" className="bg-green-500" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/teachers/${teacher.id}/approve-edit`); }}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/teachers/${teacher.id}/reject-edit`); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* School Profile Edits */}
      {pendingEdits?.schools?.length > 0 && (
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-500" />
            Perfiles Escuelas Modificados ({pendingEdits.schools.length})
          </h3>
          <div className="space-y-2">
            {pendingEdits.schools.map(school => (
              <div 
                key={school.id}
                onClick={() => onOpenPreview(school, 'school-edit', `/api/admin/schools/${school.id}/approve-edit`)}
                className="flex items-center justify-between bg-orange-50 hover:bg-orange-100 rounded-lg p-4 cursor-pointer transition-colors border-l-4 border-orange-400"
              >
                <div className="flex items-center gap-3">
                  <img src={school.images?.[0] || '/default-school.png'} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  <div>
                    <p className="font-medium">{getLocalizedText(school.name, 'es')}</p>
                    <p className="text-sm text-gray-500">{school.user_email}</p>
                    <p className="text-xs text-orange-600">
                      Modificado: {school.edit_submitted_at ? new Date(school.edit_submitted_at).toLocaleString('es-ES') : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpenPreview(school, 'school-edit', `/api/admin/schools/${school.id}/approve-edit`); }}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button size="sm" className="bg-green-500" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/schools/${school.id}/approve-edit`); }}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/schools/${school.id}/reject-edit`); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
